import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import TextField from "@/components/atoms/TextField";
import { Autocomplete } from "@/components/molecules/AutoComplete";
import {
  EMAIL_MAX_LENGTH,
  NAME_MAX_LENGTH,
  PHONE_NUMBER_MAX_LENGTH,
  sanitizePhoneNumberInput,
  type AccidentReportPersonFormError,
  type AccidentReportPersonFormInput,
} from "@/models/accidentReportPersons";
import { getOfficeOptionLabel } from "@/util";
import type { Office } from "@/models/office";
import { AccidentReportPersonsConstants as Constants } from "@/pages/AccidentReportPersons/AccidentReportPersonsConstants";

export interface AccidentReportPersonsCreateFormProps {
  inputData: AccidentReportPersonFormInput;
  offices: Office[];
  officeSelected: Office | null;
  errorData: AccidentReportPersonFormError;
  disabled?: boolean;
  onChangeData: (
    key: keyof AccidentReportPersonFormInput,
    value: string,
  ) => void;
  onChangeOffice: (value: Office | null) => void;
  // チェック時は入力済みの氏名・メール・電話番号・会社／営業所を使って
  // 国交省フロー組込み責任者にも別レコードを登録する（登録後は同期しない）。
  registerAsMlitFlow: boolean;
  onChangeRegisterAsMlitFlow: (checked: boolean) => void;
}

/**
 * 事故報告責任者の入力フォーム（登録のみ）。
 * 登録内容の変更はGO社内向け画面（halifax）でのみ行うため、本画面に変更用のモードは持たない。
 */
export const AccidentReportPersonsCreateForm = (
  props: AccidentReportPersonsCreateFormProps,
) => {
  const {
    inputData,
    offices,
    officeSelected,
    errorData,
    disabled,
    onChangeData,
    onChangeOffice,
    registerAsMlitFlow,
    onChangeRegisterAsMlitFlow,
  } = props;

  return (
    <Box>
      <Table
        size="small"
        sx={{ borderCollapse: "separate", borderSpacing: "0px 4px" }}
        className="gx-info-table"
      >
        <TableBody>
          <TableRow sx={{ height: 45 }}>
            <TableCell className="required">
              <TextField
                label={Constants.NameLabel}
                value={inputData.name}
                error={!!errorData.name}
                errorLabel={
                  errorData.name === "format"
                    ? Constants.NameFormatError
                    : Constants.NameRequiredError
                }
                onChange={(e) => onChangeData("name", e.target.value)}
                inputProps={{ maxLength: NAME_MAX_LENGTH }}
                sx={{ width: "100%" }}
                disabled={disabled}
              />
            </TableCell>
          </TableRow>
          <TableRow sx={{ height: 45 }}>
            <TableCell className="required">
              <TextField
                label={Constants.EmailLabel}
                type="email"
                value={inputData.email}
                error={!!errorData.email}
                errorLabel={
                  errorData.email === "format"
                    ? Constants.EmailFormatError
                    : Constants.EmailRequiredError
                }
                onChange={(e) => onChangeData("email", e.target.value)}
                inputProps={{ maxLength: EMAIL_MAX_LENGTH }}
                sx={{ width: "100%" }}
                disabled={disabled}
              />
            </TableCell>
          </TableRow>
          <TableRow sx={{ height: 45 }}>
            <TableCell className="required">
              <TextField
                label={Constants.PhoneLabel}
                type="tel"
                value={inputData.phoneNumber}
                error={!!errorData.phoneNumber}
                errorLabel={
                  errorData.phoneNumber === "format"
                    ? Constants.PhoneFormatError
                    : Constants.PhoneRequiredError
                }
                onChange={(e) =>
                  onChangeData(
                    "phoneNumber",
                    sanitizePhoneNumberInput(e.target.value),
                  )
                }
                inputProps={{ maxLength: PHONE_NUMBER_MAX_LENGTH }}
                sx={{ width: "100%" }}
                disabled={disabled}
              />
            </TableCell>
          </TableRow>
          <TableRow sx={{ height: 45 }}>
            <TableCell className="required">
              {/* 会社／営業所は常に1つだけ選択する（複数営業所へのまとめ登録は行わない） */}
              <Autocomplete
                options={offices}
                autoHighlight
                getOptionLabel={getOfficeOptionLabel}
                value={officeSelected}
                onChange={(_, value) => onChangeOffice(value)}
                label={Constants.OfficeLabel}
                error={!!errorData.officeId}
                errorLabel={Constants.OfficeRequiredError}
                disabled={disabled}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={registerAsMlitFlow}
                    onChange={(e) =>
                      onChangeRegisterAsMlitFlow(e.target.checked)
                    }
                    disabled={disabled}
                  />
                }
                label={Constants.MlitSimultaneousRegisterLabel}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {Constants.MlitSimultaneousRegisterCaption}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};
